import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { CustomerPicker } from "@/features/customers/components/CustomerPicker";
import { usePaymentMethods } from "@/features/paymentMethods/hooks/usePaymentMethods";
import { useActiveVarietyPricings } from "@/features/production/hooks/useVarietyPricings";
import type { VarietyPricing } from "@/features/production/services/varietyPricingApi";
import { useCreateExpoSale } from "../hooks/useExpoSales";
import { useExpo } from "../hooks/useExpos";
import { useExpoInventory } from "../hooks/useExpoTransfers";

const PRICING_MODE_PER_TRAY = 1;
const PRICING_MODE_PER_SEEDLING = 2;

const normalize = (value?: string | null) => (value ?? "").trim().toLowerCase();

const parseTrayType = (value?: string | number | null): number | null => {
  if (value === 210 || value === 442) return value;
  const normalized = normalize(String(value ?? ""));
  if (!normalized) return null;
  if (normalized.includes("210") || normalized.includes("twoten") || normalized.includes("two ten")) return 210;
  if (normalized.includes("442") || normalized.includes("fourfortytwo") || normalized.includes("four forty two")) return 442;
  return null;
};

const basePiecesPerTray = (trayType?: string | null, trayCapacity?: number | null) => {
  if (trayCapacity === 210 || trayCapacity === 442) return trayCapacity;
  const holes = parseTrayType(trayType);
  if (holes === 210) return 210;
  if (holes === 442) return 442;
  return null;
};

const stockBasePieces = (
  availableQuantity: number,
  trayType?: string | null,
  trayCapacity?: number | null,
  availableBaseQuantity?: number | null,
) => {
  const capacity = basePiecesPerTray(trayType, trayCapacity);
  if (!capacity) return null;
  return Math.round(availableBaseQuantity ?? availableQuantity * capacity);
};

const formatExpoAvailability = (item: {
  availableQuantity: number;
  availableBaseQuantity?: number | null;
  trayCapacity?: number | null;
  trayType?: string | null;
  stockUnit?: string | null;
}) => {
  const capacity = basePiecesPerTray(item.trayType, item.trayCapacity);
  if (!capacity) return `${item.availableQuantity.toLocaleString()} ${item.stockUnit ?? "units"}`;

  const availablePieces = stockBasePieces(item.availableQuantity, item.trayType, item.trayCapacity, item.availableBaseQuantity) ?? 0;
  const completeTrays = Math.floor(availablePieces / capacity);
  return `Available: ${availablePieces.toLocaleString()} pieces · ${completeTrays} complete tray${completeTrays === 1 ? "" : "s"}`;
};

const completeTrayCount = (availableQuantity: number, trayType?: string | null, trayCapacity?: number | null, availableBaseQuantity?: number | null) => {
  const capacity = basePiecesPerTray(trayType, trayCapacity);
  if (!capacity) return 0;
  const availablePieces = stockBasePieces(availableQuantity, trayType, trayCapacity, availableBaseQuantity) ?? 0;
  return Math.floor(availablePieces / capacity);
};

type SaleMode = "tray" | "piece";
type Line = { balanceId: string; quantity: number; saleMode: SaleMode };
type Tender = { accountId: string; amount: number; reference: string };

export function ExpoSalePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const { data: expo } = useExpo(id);
  const { data: inventory = [], isLoading: inventoryLoading, isError: inventoryError } = useExpoInventory(id);
  const { data: prices = [] } = useActiveVarietyPricings();
  const { data: accounts = [] } = usePaymentMethods();
  const create = useCreateExpoSale();

  const [customerId, setCustomerId] = useState("");
  const [lines, setLines] = useState<Line[]>([{ balanceId: "", quantity: 1, saleMode: "tray" }]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [error, setError] = useState("");

  const stockById = useMemo(
    () => new Map(inventory.map((item) => [item.id, item])),
    [inventory],
  );
  const availableInventory = useMemo(
    () => inventory.filter((item) => item.availableQuantity > 0),
    [inventory],
  );

  const selectedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const line of lines) {
      if (!line.balanceId) continue;
      counts.set(line.balanceId, (counts.get(line.balanceId) ?? 0) + 1);
    }
    return counts;
  }, [lines]);

  const stockQuantityForLine = (line: Line) => {
    const stock = stockById.get(line.balanceId);
    const basePieces = basePiecesPerTray(stock?.trayType, stock?.trayCapacity);
    const quantity = Number(line.quantity || 0);
    return basePieces && line.saleMode === "piece" ? quantity / basePieces : quantity;
  };

  const selectedQuantities = useMemo(() => {
    const quantities = new Map<string, number>();
    for (const line of lines) {
      if (!line.balanceId) continue;
      quantities.set(line.balanceId, (quantities.get(line.balanceId) ?? 0) + stockQuantityForLine(line));
    }
    return quantities;
  }, [lines]);

  const configuredPricingFor = (line: Line): VarietyPricing | undefined => {
    const balanceId = line.balanceId;
    const stock = stockById.get(balanceId);
    if (!stock) return undefined;
    const trayType = parseTrayType(stock.trayType);
    const pricingMode = trayType === null || line.saleMode === "piece" ? PRICING_MODE_PER_SEEDLING : PRICING_MODE_PER_TRAY;

    return prices.find(
      (price) =>
        normalize(price.cropTypeName) === normalize(stock.cropTypeName) &&
        normalize(price.varietyName) === normalize(stock.variety) &&
        price.pricingMode === pricingMode &&
        (pricingMode === PRICING_MODE_PER_TRAY ? parseTrayType(price.trayType) === trayType : price.trayType == null) &&
        price.companySeedlingPrice != null &&
        price.companySeedlingPrice > 0,
    );
  };

  const canSellPieces = (balanceId: string) =>
    !!configuredPricingFor({ balanceId, quantity: 1, saleMode: "piece" });

  const canSellCompleteTray = (balanceId: string) => {
    const stock = stockById.get(balanceId);
    return !!stock?.trayType && completeTrayCount(stock.availableQuantity, stock.trayType, stock.trayCapacity, stock.availableBaseQuantity) >= 1;
  };

  const duplicateStockIds = useMemo(
    () => [...selectedCounts.entries()].filter(([, count]) => count > 1).map(([stockId]) => stockId),
    [selectedCounts],
  );

  const overPickedStockIds = useMemo(
    () =>
      [...selectedQuantities.entries()]
        .filter(([stockId, quantity]) => quantity > (stockById.get(stockId)?.availableQuantity ?? 0))
        .map(([stockId]) => stockId),
    [selectedQuantities, stockById],
  );

  const total = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          sum + Number(line.quantity || 0) * (configuredPricingFor(line)?.companySeedlingPrice ?? 0),
        0,
      ),
    [lines, prices, stockById],
  );
  const paid = tenders.reduce((sum, tender) => sum + Number(tender.amount || 0), 0);
  const outstanding = Math.max(0, total - paid);

  const patchLine = (index: number, patch: Partial<Line>) => {
    setError("");
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const patchTender = (index: number, patch: Partial<Tender>) => {
    setTenders((current) => current.map((tender, i) => (i === index ? { ...tender, ...patch } : tender)));
  };

  const invalidLines = lines.some(
    (line) => !line.balanceId || !configuredPricingFor(line) || Number(line.quantity || 0) <= 0,
  );
  const hasDuplicates = duplicateStockIds.length > 0;
  const hasOverPicked = overPickedStockIds.length > 0;
  const cannotSubmit =
    !customerId || lines.length === 0 || invalidLines || hasDuplicates || hasOverPicked || create.isPending;

  const submit = async () => {
    if (cannotSubmit) {
      setError("Fix the highlighted sale line issues before completing the Expo sale.");
      return;
    }

    const threshold = expo?.creditApprovalThreshold ?? Number.MAX_VALUE;
    await create.mutateAsync({
      approved: hasPermission("expo_sales.credit_approve") && outstanding > threshold,
      data: {
        expoId: id,
        customerId,
        items: lines.map((line) => ({
          expoInventoryBalanceId: line.balanceId,
          varietyPricingId: configuredPricingFor(line)!.id,
          quantity: stockQuantityForLine(line),
          seedlingQuantity: line.saleMode === "piece" && basePiecesPerTray(stockById.get(line.balanceId)?.trayType, stockById.get(line.balanceId)?.trayCapacity)
            ? Number(line.quantity)
            : null,
        })),
        payments: tenders
          .filter((tender) => tender.accountId && tender.amount > 0)
          .map((tender) => ({
            paymentMethodAccountId: tender.accountId,
            amount: tender.amount,
            reference: tender.reference || undefined,
          })),
        paymentDueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
      },
    });
    navigate(`/expos/${id}`);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Button variant="ghost" asChild>
        <Link to={`/expos/${id}`}>← Back to Expo</Link>
      </Button>

      <div>
        <h1 className="text-3xl font-bold">New Expo sale</h1>
        <p className="text-muted-foreground">{expo?.name} · stock is deducted only from this Expo.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Customer</CardTitle>
          <CardDescription>Searches customers from the backend as you type.</CardDescription>
        </CardHeader>
        <CardContent>
          <CustomerPicker value={customerId} onChange={setCustomerId} title="customer" activeOnly />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
          <CardDescription>Select the Expo item being sold. Price is resolved from configured pricing.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {lines.map((line, index) => {
            const stock = stockById.get(line.balanceId);
            const pricing = configuredPricingFor(line);
            const unitPrice = pricing?.companySeedlingPrice ?? 0;
            const quantity = Number(line.quantity || 0);
            const lineTotal = unitPrice * quantity;
            const basePieces = basePiecesPerTray(stock?.trayType, stock?.trayCapacity);
            const maxSaleQuantity = basePieces && line.saleMode === "piece"
              ? stockBasePieces(stock?.availableQuantity ?? 0, stock?.trayType, stock?.trayCapacity, stock?.availableBaseQuantity) ?? 0
              : Math.floor(stock?.availableQuantity ?? 0);
            const isDuplicate = !!line.balanceId && (selectedCounts.get(line.balanceId) ?? 0) > 1;
            const isOverPicked =
              !!line.balanceId && (selectedQuantities.get(line.balanceId) ?? 0) > (stock?.availableQuantity ?? 0);
            const trayUnavailable = line.saleMode === "tray" && !!stock?.trayType && !canSellCompleteTray(line.balanceId);

            return (
              <div className="grid min-w-0 gap-2 rounded-md border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(8rem,10rem)] lg:grid-cols-[minmax(16rem,1fr)_minmax(8rem,9rem)_minmax(7rem,8rem)_minmax(10rem,13rem)_auto]" key={index}>
                <div className="min-w-0 space-y-1 sm:col-span-2 lg:col-span-1">
                  <Select
                    value={line.balanceId}
                    onValueChange={(value) => {
                      const stock = stockById.get(value);
                      patchLine(index, { balanceId: value, saleMode: stock?.trayType && completeTrayCount(stock.availableQuantity, stock.trayType, stock.trayCapacity, stock.availableBaseQuantity) >= 1 ? "tray" : "piece" });
                    }}
                  >
                    <SelectTrigger aria-invalid={isDuplicate || isOverPicked} className="w-full min-w-0">
                      <SelectValue placeholder="Select Expo item" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableInventory.length > 0 ? (
                        availableInventory.map((item) => {
                          const selectedElsewhere = lines.some(
                            (otherLine, otherIndex) => otherIndex !== index && otherLine.balanceId === item.id,
                          );
                          return (
                            <SelectItem key={item.id} value={item.id} disabled={selectedElsewhere}>
                              <span className="flex min-w-0 flex-col items-start">
                                <span className="max-w-[20rem] truncate">{item.cropTypeName}{item.variety ? ` · ${item.variety}` : ""}</span>
                                <span className="text-xs text-muted-foreground">{formatExpoAvailability(item)}</span>
                              </span>
                            </SelectItem>
                          );
                        })
                      ) : (
                        <div className="px-2 py-2 text-sm text-muted-foreground">
                          {inventoryLoading ? "Loading Expo stock..." : "No available Expo stock to sell."}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  {inventoryError && <p className="text-xs text-destructive">Unable to load Expo stock. Refresh and try again.</p>}
                  {isDuplicate && <p className="text-xs text-destructive">This Expo item is already selected.</p>}
                  {trayUnavailable && (
                    <p className="text-xs text-destructive">
                      A complete tray requires {basePieces} pieces, but only {stockBasePieces(stock?.availableQuantity ?? 0, stock?.trayType, stock?.trayCapacity, stock?.availableBaseQuantity) ?? 0} pieces are available. Sell the remaining stock as pieces.
                    </p>
                  )}
                  {isOverPicked && !trayUnavailable && (
                    <p className="text-xs text-destructive">
                      Selected quantity exceeds available {maxSaleQuantity ?? 0} {line.saleMode === "piece" ? "pieces" : stock?.trayType ?? stock?.stockUnit}.
                    </p>
                  )}
                </div>

                <Select
                  value={line.saleMode}
                  onValueChange={(value) => patchLine(index, { saleMode: value as SaleMode })}
                  disabled={!line.balanceId || !stock?.trayType}
                >
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {stock?.trayType && (
                      <SelectItem value="tray" disabled={!canSellCompleteTray(line.balanceId)}>
                        Tray{basePieces ? ` (${basePieces} pieces)` : ""}
                      </SelectItem>
                    )}
                    <SelectItem value="piece" disabled={!!stock?.trayType && !canSellPieces(line.balanceId)}>
                      Pieces
                    </SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="number"
                  min="1"
                  max={maxSaleQuantity}
                  step="1"
                  value={line.quantity}
                  onChange={(event) => patchLine(index, { quantity: Number(event.target.value) })}
                  aria-invalid={isOverPicked || trayUnavailable || quantity <= 0}
                  placeholder={line.saleMode === "piece" ? "Pieces" : "Qty"}
                />

                <div className="min-w-0 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  {!line.balanceId ? (
                    <span className="text-muted-foreground">Select item</span>
                  ) : pricing ? (
                    <div>
                      <p className="truncate">KES {lineTotal.toLocaleString()}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {quantity.toLocaleString()} {line.saleMode === "piece" ? "pieces" : stock?.trayType ?? stock?.stockUnit} × KES {unitPrice.toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <span className="text-destructive">Price not configured</span>
                  )}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                  className="w-full sm:w-auto lg:justify-self-end"
                >
                  Remove
                </Button>
              </div>
            );
          })}

          {!inventoryLoading && availableInventory.length === 0 && (
            <Alert>
              <AlertTitle>No sellable Expo stock</AlertTitle>
              <AlertDescription>
                This sale form only lists stock received into this Expo with a positive available balance. Receive stock first, or check whether stock is already sold, returned, or reserved for a pending return.
              </AlertDescription>
            </Alert>
          )}

          <Button type="button" variant="outline" onClick={() => setLines((current) => [...current, { balanceId: "", quantity: 1, saleMode: "tray" }])}>
            Add item
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment</CardTitle>
          <CardDescription>
            Total KES {total.toFixed(2)} · Outstanding KES {outstanding.toFixed(2)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {tenders.map((tender, index) => (
            <div className="grid gap-2 md:grid-cols-[1fr_140px_1fr_110px]" key={index}>
              <Select value={tender.accountId} onValueChange={(value) => patchTender(index, { accountId: value })}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Payment method" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="Amount"
                value={tender.amount || ""}
                onChange={(event) => patchTender(index, { amount: Number(event.target.value) })}
              />
              <Input
                placeholder="M-Pesa/bank reference"
                value={tender.reference}
                onChange={(event) => patchTender(index, { reference: event.target.value })}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setTenders((current) => current.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </div>
          ))}

          <Button type="button" variant="outline" onClick={() => setTenders((current) => [...current, { accountId: "", amount: 0, reference: "" }])}>
            Record payment
          </Button>

          {outstanding > 0 && (
            <div className="max-w-xs space-y-2">
              <Label>Paid-later due date</Label>
              <DatePicker value={dueDate} onChange={setDueDate} placeholder="Pick due date" />
            </div>
          )}
        </CardContent>
      </Card>

      {error && <p className="text-sm font-medium text-destructive">{error}</p>}

      <Button disabled={cannotSubmit} onClick={submit}>
        Complete Expo sale
      </Button>
    </div>
  );
}
